import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/hooks/use-toast';
import { 
  useAlphaQuantSignals, 
  AlphaQuantConfig, 
  DEFAULT_ALPHA_QUANT_CONFIG,
  AlphaQuantSignal,
  AlphaQuantTrade 
} from '@/hooks/useAlphaQuantSignals';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Shield, 
  Activity,
  ChevronDown,
  ChevronRight,
  Play,
  Settings,
  BarChart3,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Percent,
  LineChart
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine
} from 'recharts';

interface AlphaQuantPanelProps {
  onSignalsUpdate?: (signals: AlphaQuantSignal[], trades: AlphaQuantTrade[], data?: import('@/services/binanceService').EnrichedCandleData[]) => void;
}

export const AlphaQuantPanel = ({ onSignalsUpdate }: AlphaQuantPanelProps) => {
  const {
    isLoading,
    data,
    signals,
    trades,
    results,
    error,
    config,
    setConfig,
    loadData,
    runBacktest,
  } = useAlphaQuantSignals();
  
  const [localConfig, setLocalConfig] = useState<AlphaQuantConfig>(DEFAULT_ALPHA_QUANT_CONFIG);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Format helpers
  const formatPercent = (value: number | undefined | null, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
  };
  
  const formatNumber = (value: number | undefined | null, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '—';
    return value.toFixed(decimals);
  };
  
  const formatUSD = (value: number | undefined | null) => {
    if (value === undefined || value === null || isNaN(value)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };
  
  // Handle config change
  const updateConfig = (key: keyof AlphaQuantConfig, value: any) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };
  
  // Run backtest handler
  const handleRunBacktest = async () => {
    try {
      const result = await runBacktest(localConfig, 10000);
      toast({
        title: 'Backtest Completo',
        description: `${result?.signals.length || 0} sinais detectados em 30 dias`,
      });
      
      if (onSignalsUpdate && result) {
        onSignalsUpdate(result.signals, result.trades, result.candles);
      }
    } catch (err) {
      toast({
        title: 'Erro no Backtest',
        description: err instanceof Error ? err.message : 'Falha ao executar backtest',
        variant: 'destructive',
      });
    }
  };
  
  // Signal type colors
  const getSignalColor = (type: string) => {
    switch (type) {
      case 'L-SQ': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'REC': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'TOP-REV': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'BOT-REV': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      default: return 'bg-muted text-muted-foreground';
    }
  };
  
  const getDirectionIcon = (direction: string) => {
    return direction === 'long' 
      ? <TrendingUp className="h-4 w-4 text-green-400" />
      : <TrendingDown className="h-4 w-4 text-red-400" />;
  };
  
  // Equity curve data for chart
  const equityChartData = useMemo(() => {
    if (!results?.equityCurve) return [];
    
    // Sample every 6 hours for cleaner chart
    return results.equityCurve.filter((_, i) => i % 6 === 0).map(point => ({
      date: point.date,
      value: point.value,
      return: ((point.value - 10000) / 10000) * 100,
    }));
  }, [results]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-yellow-400" />
            AlphaQuant Signal
          </h2>
          <p className="text-muted-foreground text-sm">
            Estratégia de derivativos com Macro Filters (NFCI / HY Spread / SSR)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            <Clock className="h-3 w-3 mr-1" />
            30 dias
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {data.length > 0 ? `${data.length} candles` : 'Sem dados'}
          </Badge>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="text-xs">
            <BarChart3 className="h-4 w-4 mr-1" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="config" className="text-xs">
            <Settings className="h-4 w-4 mr-1" />
            Configuração
          </TabsTrigger>
          <TabsTrigger value="signals" className="text-xs">
            <Activity className="h-4 w-4 mr-1" />
            Sinais
          </TabsTrigger>
          <TabsTrigger value="trades" className="text-xs">
            <Target className="h-4 w-4 mr-1" />
            Trades
          </TabsTrigger>
          <TabsTrigger value="debug" className="text-xs">
            <AlertTriangle className="h-4 w-4 mr-1" />
            Debug
          </TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Quick Action */}
          <Card className="border-dashed border-2 border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="font-semibold">Executar Backtest</h3>
                  <p className="text-sm text-muted-foreground">
                    Carregar dados da Binance e simular estratégia nos últimos 30 dias
                  </p>
                </div>
                <Button 
                  onClick={handleRunBacktest} 
                  disabled={isLoading}
                  size="lg"
                  className="gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Executar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Results Grid */}
          {results && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">Retorno Total</div>
                      <Percent className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className={cn(
                      "text-2xl font-bold mt-1",
                      results.totalReturn >= 0 ? 'text-green-400' : 'text-red-400'
                    )}>
                      {formatPercent(results.totalReturn)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatUSD(results.finalCapital)} final
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">Win Rate</div>
                      <Target className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className={cn(
                      "text-2xl font-bold mt-1",
                      results.winRate >= 50 ? 'text-green-400' : 'text-orange-400'
                    )}>
                      {formatPercent(results.winRate, 1)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {results.totalTrades} trades
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">Max Drawdown</div>
                      <TrendingDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold mt-1 text-red-400">
                      -{formatNumber(results.maxDrawdown)}%
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Risco máximo
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">Profit Factor</div>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className={cn(
                      "text-2xl font-bold mt-1",
                      results.profitFactor >= 1.5 ? 'text-green-400' : 
                      results.profitFactor >= 1 ? 'text-yellow-400' : 'text-red-400'
                    )}>
                      {formatNumber(results.profitFactor)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Sharpe: {formatNumber(results.sharpeRatio)}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Equity Curve */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <LineChart className="h-4 w-4" />
                    Curva de Capital
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={equityChartData}>
                        <defs>
                          <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 10 }}
                          tickFormatter={(value) => value.slice(5)} 
                          className="text-muted-foreground"
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          tickFormatter={(value) => `$${(value/1000).toFixed(1)}k`}
                          className="text-muted-foreground"
                        />
                        <Tooltip
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value: number) => [formatUSD(value), 'Capital']}
                        />
                        <ReferenceLine y={10000} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="hsl(var(--primary))"
                          fill="url(#equityGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Signal Breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Breakdown por Sinal</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: 'lsq', label: 'L-SQ', data: results.signalBreakdown.lsq },
                      { key: 'rec', label: 'REC', data: results.signalBreakdown.rec },
                      { key: 'topRev', label: 'TOP-REV', data: results.signalBreakdown.topRev },
                      { key: 'botRev', label: 'BOT-REV', data: results.signalBreakdown.botRev },
                    ].map(item => (
                      <div key={item.key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className={getSignalColor(item.label)}>
                            {item.label}
                          </Badge>
                          <span className="text-sm font-medium">{item.data.count}</span>
                        </div>
                        <Progress value={item.data.winRate} className="h-2" />
                        <div className="text-xs text-muted-foreground text-center">
                          {formatPercent(item.data.winRate, 0)} win rate
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          
          {!results && !isLoading && (
            <Card className="border-dashed">
              <CardContent className="pt-6 text-center">
                <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">Nenhum Resultado</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Execute o backtest para ver os resultados da estratégia
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Signal Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Sinais Ativos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: 'enable_lsq', label: 'L-SQ (Long Squeeze)', desc: 'SHORT quando OI↑ + Price band' },
                  { key: 'enable_rec', label: 'REC (Recovery)', desc: 'LONG quando OI↓ + Price hold' },
                  { key: 'enable_top_rev', label: 'TOP-REV', desc: 'SHORT em reversão de topo' },
                  { key: 'enable_bot_rev', label: 'BOT-REV', desc: 'LONG em reversão de fundo' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">{item.label}</Label>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch
                      checked={localConfig[item.key as keyof AlphaQuantConfig] as boolean}
                      onCheckedChange={(checked) => updateConfig(item.key as keyof AlphaQuantConfig, checked)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Thresholds 24H */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Thresholds 24H</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">OI Up (L-SQ) %</Label>
                    <Input
                      type="number"
                      value={localConfig.oi_up_lsq}
                      onChange={(e) => updateConfig('oi_up_lsq', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">OI Down (REC) %</Label>
                    <Input
                      type="number"
                      value={localConfig.oi_down_rec}
                      onChange={(e) => updateConfig('oi_down_rec', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Banda Preço Low %</Label>
                    <Input
                      type="number"
                      value={localConfig.px_lo}
                      onChange={(e) => updateConfig('px_lo', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Banda Preço High %</Label>
                    <Input
                      type="number"
                      value={localConfig.px_hi}
                      onChange={(e) => updateConfig('px_hi', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Funding L-SQ (bps)</Label>
                    <Input
                      type="number"
                      value={localConfig.fund_bp_lsq}
                      onChange={(e) => updateConfig('fund_bp_lsq', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Funding REC (bps)</Label>
                    <Input
                      type="number"
                      value={localConfig.fund_bp_rec}
                      onChange={(e) => updateConfig('fund_bp_rec', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Stop/TP */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Stop Loss / Take Profit</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">L-SQ SL %</Label>
                    <Input
                      type="number"
                      value={localConfig.lsq_sl_pct}
                      onChange={(e) => updateConfig('lsq_sl_pct', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">L-SQ TP %</Label>
                    <Input
                      type="number"
                      value={localConfig.lsq_tp_pct}
                      onChange={(e) => updateConfig('lsq_tp_pct', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">TOP-REV SL %</Label>
                    <Input
                      type="number"
                      value={localConfig.top_sl_pct}
                      onChange={(e) => updateConfig('top_sl_pct', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">TOP-REV TP %</Label>
                    <Input
                      type="number"
                      value={localConfig.top_tp_pct}
                      onChange={(e) => updateConfig('top_tp_pct', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">BOT-REV SL %</Label>
                    <Input
                      type="number"
                      value={localConfig.bot_sl_pct}
                      onChange={(e) => updateConfig('bot_sl_pct', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">BOT-REV TP %</Label>
                    <Input
                      type="number"
                      value={localConfig.bot_tp_pct}
                      onChange={(e) => updateConfig('bot_tp_pct', parseFloat(e.target.value))}
                      className="h-8"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Trailing */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Trailing Stop</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Usar Trailing Stop</Label>
                  <Switch
                    checked={localConfig.use_trailing}
                    onCheckedChange={(checked) => updateConfig('use_trailing', checked)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Ativar em %</Label>
                    <Input
                      type="number"
                      value={localConfig.trailing_start}
                      onChange={(e) => updateConfig('trailing_start', parseFloat(e.target.value))}
                      className="h-8"
                      disabled={!localConfig.use_trailing}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Distância %</Label>
                    <Input
                      type="number"
                      value={localConfig.trailing_dist}
                      onChange={(e) => updateConfig('trailing_dist', parseFloat(e.target.value))}
                      className="h-8"
                      disabled={!localConfig.use_trailing}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Advanced Config */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span>Configurações Avançadas (Reversões)</span>
                {showAdvanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* TOP-REV Config */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-orange-400">TOP-REV</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">ΔPreço 24h ≥ %</Label>
                        <Input
                          type="number"
                          value={localConfig.top_px_up_24}
                          onChange={(e) => updateConfig('top_px_up_24', parseFloat(e.target.value))}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Diff 24h ≤ (p.p.)</Label>
                        <Input
                          type="number"
                          value={localConfig.top_diff_neg_24}
                          onChange={(e) => updateConfig('top_diff_neg_24', parseFloat(e.target.value))}
                          className="h-8"
                        />
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Triggers</Label>
                      {[
                        { key: 'use_trg_diff_top', label: 'Diff ↓ +diff_pp' },
                        { key: 'use_trg_fund_top', label: 'ΔFunding 1-2h ≤ 0' },
                        { key: 'use_trg_oi_top', label: 'ΔOI 1-2h ≤ 0' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center justify-between">
                          <Label className="text-xs">{item.label}</Label>
                          <Switch
                            checked={localConfig[item.key as keyof AlphaQuantConfig] as boolean}
                            onCheckedChange={(checked) => updateConfig(item.key as keyof AlphaQuantConfig, checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* BOT-REV Config */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-blue-400">BOT-REV</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">ΔPreço 24h ≤ − %</Label>
                        <Input
                          type="number"
                          value={localConfig.bot_px_dn_24}
                          onChange={(e) => updateConfig('bot_px_dn_24', parseFloat(e.target.value))}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Diff 24h ≥ (p.p.)</Label>
                        <Input
                          type="number"
                          value={localConfig.bot_diff_pos_24}
                          onChange={(e) => updateConfig('bot_diff_pos_24', parseFloat(e.target.value))}
                          className="h-8"
                        />
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Triggers</Label>
                      {[
                        { key: 'use_trg_diff_bot', label: 'Diff ↑ −diff_pp' },
                        { key: 'use_trg_fund_bot', label: 'ΔFunding 1-2h ≥ 0' },
                        { key: 'use_trg_oi_bot', label: 'OI Flush ≤ −E%' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center justify-between">
                          <Label className="text-xs">{item.label}</Label>
                          <Switch
                            checked={localConfig[item.key as keyof AlphaQuantConfig] as boolean}
                            onCheckedChange={(checked) => updateConfig(item.key as keyof AlphaQuantConfig, checked)}
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <Label className="text-xs">E (flush OI) %</Label>
                        <Input
                          type="number"
                          value={localConfig.bot_oi_flush_E}
                          onChange={(e) => updateConfig('bot_oi_flush_E', parseFloat(e.target.value))}
                          className="h-8"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Apply Button */}
          <div className="flex justify-end">
            <Button onClick={handleRunBacktest} disabled={isLoading} className="gap-2">
              <Play className="h-4 w-4" />
              Aplicar e Executar
            </Button>
          </div>
        </TabsContent>
        
        {/* Signals Tab */}
        <TabsContent value="signals" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Sinais Detectados ({signals.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {signals.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {signals.map((signal, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {getDirectionIcon(signal.direction)}
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={getSignalColor(signal.type)}>
                                {signal.type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(signal.timestamp).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {signal.triggers.join(' • ')}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            ${signal.price.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            SL: ${signal.stopLoss.toFixed(0)} | TP: ${signal.takeProfit.toFixed(0)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum sinal detectado</p>
                  <p className="text-xs">Execute o backtest para ver os sinais</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Trades Tab */}
        <TabsContent value="trades" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Histórico de Trades ({trades.filter(t => t.type === 'exit').length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trades.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {trades.filter(t => t.type === 'exit').map((trade, idx) => {
                      const isWin = (trade.pnl || 0) > 0;
                      return (
                        <div 
                          key={idx}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border",
                            isWin ? 'bg-green-500/5 border-green-500/30' : 'bg-red-500/5 border-red-500/30'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {isWin ? (
                              <CheckCircle2 className="h-5 w-5 text-green-400" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-400" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={getSignalColor(trade.signalType)}>
                                  {trade.signalType}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {trade.side.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {new Date(trade.timestamp).toLocaleString('pt-BR')}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={cn(
                              "font-bold",
                              isWin ? 'text-green-400' : 'text-red-400'
                            )}>
                              {formatPercent(trade.pnl)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {trade.exitReason}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum trade executado</p>
                  <p className="text-xs">Execute o backtest para ver os trades</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Debug Tab */}
        <TabsContent value="debug" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-400" />
                    Dados Calculados (1H OI/Funding)
                  </CardTitle>
                  <CardDescription>
                    Compare estes valores com o TradingView para validar (OI busca em 1H como Pine Script)
                  </CardDescription>
                </div>
                {data.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Export CSV
                      const headers = ['Date', 'Close', 'PriceChange24h', 'OI', 'OIChange24h', 'Diff24h', 'Funding24h_bps', 'FundingDelta_bps'];
                      const rows = data.map(c => [
                        c.date.slice(0, 10),
                        c.close.toFixed(2),
                        (c.priceChange24h || 0).toFixed(4),
                        (c.openInterest || 0).toFixed(0),
                        (c.oiChange24h || 0).toFixed(4),
                        (c.priceDiff24h || 0).toFixed(4),
                        ((c.funding24h || 0) * 10000).toFixed(4),
                        ((c.fundingDelta24h || 0) * 10000).toFixed(4)
                      ]);
                      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `alphaquant_data_${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast({ title: 'CSV Exportado', description: `${data.length} candles exportados` });
                    }}
                    className="gap-1"
                  >
                    <Activity className="h-3 w-3" />
                    Exportar CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {data.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-1">
                    <div className="grid grid-cols-8 gap-2 text-xs font-medium text-muted-foreground border-b pb-2 sticky top-0 bg-background">
                      <div>Data</div>
                      <div>Preço</div>
                      <div>ΔPreço 24h</div>
                      <div>OI (M)</div>
                      <div>ΔOI 24h</div>
                      <div>Diff</div>
                      <div>Fund 24h</div>
                      <div>ΔFund</div>
                    </div>
                    {data.slice(-30).reverse().map((candle, idx) => {
                      const funding24h_bps = (candle.funding24h || 0) * 10000;
                      const fundingDelta_bps = (candle.fundingDelta24h || 0) * 10000;
                      return (
                        <div key={idx} className="grid grid-cols-8 gap-2 text-xs py-1 border-b border-muted/20">
                          <div className="font-mono">{candle.date.slice(0, 10)}</div>
                          <div>${candle.close.toLocaleString()}</div>
                          <div className={candle.priceChange24h >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {candle.priceChange24h?.toFixed(2) || '—'}%
                          </div>
                          <div>{((candle.openInterest || 0) / 1e6).toFixed(2)}</div>
                          <div className={(candle.oiChange24h || 0) >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {candle.oiChange24h?.toFixed(2) || '—'}%
                          </div>
                          <div className={(candle.priceDiff24h || 0) >= 0 ? 'text-blue-400' : 'text-orange-400'}>
                            {candle.priceDiff24h?.toFixed(2) || '—'}
                          </div>
                          <div className={funding24h_bps >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {funding24h_bps.toFixed(1)} bps
                          </div>
                          <div className={fundingDelta_bps >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {fundingDelta_bps.toFixed(1)} bps
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Sem dados para exibir</p>
                  <p className="text-xs">Execute o backtest para carregar os dados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Error Display */}
      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AlphaQuantPanel;
